/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { debounce, get, toString } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { Label, Text, TextInput, LoadingSpinner } from '@blend/enterprise-components';

import { TypeaheadResultComponent, TypeaheadResults } from './TypeaheadResults';

const styles = StyleSheet.create({
  root: {
    position: 'relative',
  },
  spinnerContainer: {
    bottom: 3,
    position: 'absolute',
    right: '3%',
    transform: 'translateY(-50%)',
  },
});

const DEBOUNCE_MS = 250;

export type SearchFunction = (q: string) => Promise<{ data: Record<string, any>[] }>;

interface searchParams {
  query: string;
  searchFunction: SearchFunction;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setResults: React.Dispatch<React.SetStateAction<Record<string, any>[]>>;
}
const debounceSearch = debounce(async ({ query, setError, setResults, setIsLoading, searchFunction }: searchParams) => {
  setIsLoading(true);
  setError(null);
  try {
    const { data } = await searchFunction(query);
    setResults(data);
  } catch (e) {
    setError(get(e, 'message', toString(e)));
    setResults([]);
  }
  setIsLoading(false);
}, DEBOUNCE_MS);

export namespace TypeaheadSearch {
  export interface Props {
    ResultComponent: React.FC<TypeaheadResultComponent.Props>;
    clearOnSelect?: boolean;
    id?: string;
    inputThreshold?: number;
    isInModal?: boolean;
    label?: string;
    maxResultsHeight?: number;
    name: string;
    onResultClick: (result: any) => void;
    placeholder?: string;
    searchFunction: SearchFunction;
  }
}

export const TypeaheadSearch: React.FC<TypeaheadSearch.Props> = ({
  clearOnSelect = true,
  id,
  isInModal,
  label,
  maxResultsHeight,
  inputThreshold = 1,
  name,
  onResultClick,
  placeholder = 'Search',
  ResultComponent,
  searchFunction,
}) => {
  const [query, setQuery] = useState<string>('');
  const [searchError, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInputFocused, setInputFocused] = useState<boolean>(true);
  const [results, setResults] = useState<Record<string, any>[]>([]);
  const isMounted = useRef(false);

  useEffect(() => {
    const executeSearch = async () => {
      if (isMounted.current) {
        if (!!query && query.length >= inputThreshold) {
          await debounceSearch({ query, searchFunction, setError, setIsLoading, setResults });
        } else {
          setError(null);
          setResults([]);
        }
      } else {
        isMounted.current = true;
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    executeSearch();
  }, [query, setResults, debounceSearch, searchFunction, setIsLoading]);

  return (
    <div
      className={css(styles.root)}
      id={`it-${name}-search`}
      onBlur={e => {
        const currentTarget = e.currentTarget;
        const nextTarget = e.relatedTarget;

        if (nextTarget instanceof Element && currentTarget.contains(nextTarget)) return;
        setInputFocused(false);
      }}
      onFocus={() => setInputFocused(true)}
    >
      {label ? <Label labelSize="sm">{label}</Label> : null}
      <TextInput
        autoComplete="off"
        configOverrides={{
          icon: {
            position: 'left',
            size: 16,
            type: 'search',
          },
        }}
        id={id}
        onChange={e => setQuery(e.target.value)}
        placeholder={placeholder}
        type="search"
        value={query}
      />

      {isLoading && (
        <div className={css(styles.spinnerContainer)}>
          <LoadingSpinner size="md" theme="secondary" />
        </div>
      )}
      {searchError ? (
        <label htmlFor={id}>
          <Text colorName="ACCENT_RED_70" fontSize="sm">
            Error during search. Please try again later.
          </Text>
        </label>
      ) : null}
      <TypeaheadResults
        ResultComponent={ResultComponent}
        isInModal={isInModal}
        isInputFocused={isInputFocused}
        key={query}
        maxResultsHeight={maxResultsHeight}
        name={name}
        onResultClick={result => {
          onResultClick(result);
          if (clearOnSelect) {
            setQuery('');
          }
        }}
        results={results}
      />
    </div>
  );
};
